import { Actor, ActorSubclass, HttpAgent } from '@dfinity/agent'
import { idlFactory as odinActor } from '../idls/odinBackend/odinBackend.did'
import type { _SERVICE as OdinActor } from '../idls/odinBackend/odinBackend.did'
import { idlFactory as odinTradeActor, TradeType } from '../idls/odinTrade/odinTrade.did'
import type { _SERVICE as OdinTradeActor, TradeAmount } from '../idls/odinTrade/odinTrade.did'
import { ODIN_BACKEND_CANISTER } from '../constants/canisterIds'
import { OdinToken } from '../interfaces/common'
import {
  SwapInfo,
  SwapProvider,
  SwapStep,
  SwapInterface,
  SwapTokenPair,
  SwapMode,
} from '../interfaces/swapInterface'
import { Principal } from '@dfinity/principal'
import { ODINBTC_ID } from '../constants/tokenIds'
import { PriceQuote } from '../interfaces/tokenPriceInfo'

const ODIN_API_URL = 'https://api.odin.fun/v1'
const ODIN_TRADE_CANISTER = 'z2vm5-gaaaa-aaaaj-azw6q-cai'

type OdinApiToken = {
  id: string
  ticker: string
  rune: string
  name: string
  balance: number
  image: string | null
  divisibility: number
  decimals: number
  rune_id: string
  trading: boolean
  deposits: boolean
  withdrawals: boolean
}

type GetBalancesResponse = {
  data: OdinApiToken[]
  page: number
  limit: number
  count: number
}

type TokenInfo = {
  id: string
  name: string
  description: string
  image: string
  creator: string
  created_time: string
  volume: number
  bonded: boolean
  icrc_ledger: string
  price: number
  marketcap: number
  rune: string
  featured: boolean
  holder_count: number
  holder_top: number
  holder_dev: number
  comment_count: number
  sold: number
  twitter: string
  website: string
  telegram: string
  last_comment_time: string
  sell_count: number
  buy_count: number
  ticker: string
  btc_liquidity: number
  token_liquidity: number
  user_btc_liquidity: number
  user_token_liquidity: number
  user_lp_tokens: number
  total_supply: number
  swap_fees: number
  swap_fees_24: number
  swap_volume: number
  swap_volume_24: number
  threshold: number
  txn_count: number
  divisibility: number
  decimals: number
  withdrawals: boolean
  deposits: boolean
  trading: boolean
  external: boolean
  price_5m: number
  price_1h: number
  price_6h: number
  price_1d: number
  rune_id: string
  last_action_time: string
  twitter_verified: boolean
  verified: boolean
  tags: string[]
}

type TokenPriceCandle = {
  time: number
  open: number
  high: number
  low: number
  close: number
  volume: number
}

type TokenPriceHistoryResponse = TokenPriceCandle[]

const calculateAMMPriceChange = (data: TokenInfo, amountInSats: number, buy: boolean) => {
  const btcLiquidity = data.btc_liquidity / 1e8
  const tokenLiquidity = data.token_liquidity / 10 ** data.divisibility
  const price = data.price / 1e8

  let expected, newBtcLiquidity, newTokenLiquidity, actual
  const k = btcLiquidity * tokenLiquidity
  let quote = 0

  if (buy) {
    const amountInBtc = amountInSats / 1e8
    expected = amountInBtc / price // amount in tokens
    newBtcLiquidity = btcLiquidity + amountInBtc
    newTokenLiquidity = k / newBtcLiquidity
    actual = (tokenLiquidity - newTokenLiquidity) / 10 ** data.decimals
    quote = Number(actual * 10 ** (data.decimals + data.divisibility))
  } else {
    const amountInTokens = amountInSats / 10 ** data.divisibility
    expected = (amountInTokens * price) / 1e6 // amount in btc
    newTokenLiquidity = tokenLiquidity + amountInTokens
    newBtcLiquidity = k / newTokenLiquidity
    actual = (btcLiquidity - newBtcLiquidity) / 10 ** data.decimals
    quote = Number(actual * 1e11) // btc is always 11 decimals
  }

  const slippage = ((expected - actual) / expected) * 100

  const info: SwapInfo = {
    quote,
    slippage: Math.max(slippage, 0),
  }
  return info
}

class OdinClient implements SwapInterface<OdinToken> {
  public agent: HttpAgent
  private backendHelperActor: ActorSubclass<OdinActor>
  private tradeHelperActor: ActorSubclass<OdinTradeActor>

  constructor(agent: HttpAgent) {
    this.agent = agent
    this.tradeHelperActor = Actor.createActor(odinTradeActor, {
      canisterId: ODIN_TRADE_CANISTER,
      agent,
    }) as ActorSubclass<OdinTradeActor>

    this.backendHelperActor = Actor.createActor(odinActor, {
      canisterId: ODIN_BACKEND_CANISTER,
      agent,
    }) as ActorSubclass<OdinActor>
  }

  static async request(path: string) {
    const response = await fetch(`${ODIN_API_URL}/${path}`, {
      headers: {
        'User-Agent': 'curl/8.0.1',
      },
    })

    if (response.ok) {
      return await response.json()
    } else {
      throw await response.text()
    }
  }

  static async getBalances(address: string, ids: string[]): Promise<OdinApiToken[]> {
    const path = `user/${address}/balances`
    const response: GetBalancesResponse = await OdinClient.request(path)

    if (ids.length > 0) {
      return response.data.filter((asset) => ids.includes(asset.id))
    }
    return response.data
  }

  async getQuoteOdin(
    fromToken: OdinToken,
    toToken: OdinToken,
    amountInSats: number
  ): Promise<SwapInfo> {
    const token = fromToken.id === ODINBTC_ID ? toToken : fromToken
    const response: TokenInfo = await OdinClient.request(`token/${token.id}?show_hidden=true`)

    if (response.bonded) {
      return calculateAMMPriceChange(
        response,
        amountInSats,
        fromToken.id === ODINBTC_ID ? true : false
      )
    }
    throw 'Not supported' // Bonding curve tokens not supported
  }

  async getPair(fromToken: OdinToken, toToken: OdinToken) {
    if (toToken.id !== ODINBTC_ID && fromToken.id !== ODINBTC_ID)
      return Promise.reject('Not supported')

    const token = fromToken.id === ODINBTC_ID ? toToken : fromToken
    const response: TokenInfo = await OdinClient.request(`token/${token.id}?show_hidden=true`)

    return {
      swapCanisterPrincipal: ODIN_TRADE_CANISTER,
      feeRate: response.swap_fees,
      token0: {
        principal: fromToken.id,
        standard: 'odin',
      },
      token1: {
        principal: toToken.id,
        standard: 'odin',
      },
      provider: SwapProvider.ODIN,
      paused: false,
    }
  }

  static async fetchTokenPriceHistory(
    tokenId: string,
    startDate: Date,
    endDate: Date,
    price: number
  ): Promise<PriceQuote[]> {
    const startTs = startDate.getTime()
    const endTs = endDate.getTime()
    const durationMs = endDate.getTime() - startDate.getTime()
    const durationMinutes = durationMs / (60 * 1000)
    let minutes = Math.ceil(durationMinutes / 300)

    if (minutes <= 1) minutes = 1
    else if (minutes <= 5) minutes = 5
    else if (minutes <= 15) minutes = 15
    else if (minutes <= 30) minutes = 30
    else if (minutes <= 60) minutes = 60
    else minutes = 120

    const response: TokenPriceHistoryResponse = await OdinClient.request(
      `token/${tokenId}/feed?resolution=${minutes}&from=${startTs}&to=${endTs}`
    )

    return response.map((candle) => ({
      timestamp: candle.time * 1000,
      price: (candle.close / 1e11) * price,
    }))
  }

  async depositCkBTC(height: number) {
    return this.backendHelperActor.ckbtc_deposit([], BigInt(height))
  }

  async transferToken(to: string, tokenId: string, amount: string) {
    return this.tradeHelperActor.token_transfer({
      to,
      tokenid: tokenId,
      amount: BigInt(amount),
    })
  }

  async withdrawToken(tokenId: string, to: string, amount: string) {
    return this.tradeHelperActor.token_withdraw({
      protocol: { ckbtc: null },
      tokenid: tokenId,
      address: to,
      amount: BigInt(amount),
    })
  }

  async swap(
    _swapCanisterPrincipal: string,
    _mode: SwapMode,
    fromToken: OdinToken,
    toToken: OdinToken,
    amount: number,
    _limitPrice: number,
    _maxSlippage: number,
    setStep: (step: SwapStep) => void
  ): Promise<number> {
    const isBtcFrom = fromToken.id === ODINBTC_ID
    const tradeType: TradeType = isBtcFrom ? { buy: null } : { sell: null }
    const token = isBtcFrom ? toToken : fromToken
    const tradeAmount: TradeAmount = isBtcFrom
      ? { btc: BigInt(amount) } // buying tokens with BTC
      : { token: BigInt(amount) } // selling tokens for BTC

    const swapResponse = await this.tradeHelperActor.token_trade({
      tokenid: token.id,
      typeof: tradeType,
      settings: [],
      amount: tradeAmount,
    })

    if ('ok' in swapResponse) {
      return 1
    }

    if ('err' in swapResponse) {
      setStep(SwapStep.ErrorFallback)
      return Promise.reject(swapResponse.err)
    }

    return Promise.reject('unknown error')
  }

  async getPoolUnusedBalance(pool: SwapTokenPair, userPrincipal: string) {
    return Promise.reject('Not supported')
  }
  async getQuote(swapCanisterPrincipal: string, mode: SwapMode, amount: number) {
    return Promise.reject('Not supported')
  }
  getDepositAddress(swapCanisterPrincipal: string, principal: Principal) {
    return Promise.reject('Not supported')
  }
  getSwapPairEvents(swapPairCanisterPrincipal: string, accountPrincipal: string) {
    return Promise.reject('Not supported')
  }
  getPairs() {
    return Promise.reject('Not implemented')
  }
  fallback(swapCanisterPrincipal: string, subaccount: number[]) {
    return Promise.reject('Not supported')
  }
}

export default OdinClient
